import React, { useEffect } from 'react';
import { SEO } from '../components/SEO';
import { Link } from 'react-router-dom';
import { 
  FaMapMarkerAlt, 
  FaPhone, 
  FaEnvelope, 
  FaWhatsapp, 
  FaFacebookF, 
  FaLinkedinIn, 
  FaYoutube, 
  FaInstagram,
  FaArrowRight,
  FaBuilding,
  FaFileAlt,
  FaShieldAlt,
  FaInfoCircle
} from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';

const ContactInfo = () => {
  // Reset scroll position when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const socialLinks = [
    { name: 'Facebook', icon: <FaFacebookF />, url: 'https://www.facebook.com/ECOSOFTOPUS' },
    { name: 'X', icon: <FaXTwitter />, url: 'https://x.com/OPUSECOSOFT' },
    { name: 'LinkedIn', icon: <FaLinkedinIn />, url: 'https://www.linkedin.com/in/ecosoftopus/' },
    { name: 'YouTube', icon: <FaYoutube />, url: 'https://www.youtube.com/@OPUSInfo/featured' },
    { name: 'Instagram', icon: <FaInstagram />, url: 'https://www.instagram.com/opusinfo/' }
  ];

  const companyLinks = [
    { name: 'Aviso de privacidad', icon: <FaShieldAlt />, url: '/privacidad' },
    { name: 'Notas comerciales', icon: <FaFileAlt />, url: '/notas-comerciales' },
    { name: 'Contrato de licencia de uso', icon: <FaFileAlt />, url: '/licencia' },
    { name: 'Acerca de ECOSOFT', icon: <FaInfoCircle />, url: '/nosotros' },
    { name: 'Soporte Técnico', icon: <FaWhatsapp />, url: 'https://wa.me/525579658522', isExternal: true }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Contacto | Ecosoft"
        description="Información de contacto de Ecosoft. Contáctenos para cualquier consulta sobre OPUS, servicios o soporte técnico."
        name="ContactInfo"
        type="website"
      />
      
      {/* Hero Section */}
      <section id="hero" className="relative h-[450px] bg-[#003171]">
        <div 
          className="absolute inset-0 bg-cover bg-center" 
          style={{ 
            backgroundImage: "url('/images/contacto.webp')",
            backgroundPosition: "center center",
            backgroundSize: "cover"
          }}
        ></div>
        <div className="absolute inset-0 bg-black/70"></div>
        <div className="container mx-auto px-4 h-full flex items-center pt-14 relative z-10">
          <div className="max-w-2xl text-white">
            <h1 className="text-5xl font-bold mb-4">Contacto</h1>
            <p className="text-xl mb-8">
              ¿Necesitas ayuda? Contáctanos y nos pondremos en contacto contigo a la brevedad.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="https://wa.me/525579658522"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center bg-[#0066CC] hover:bg-[#004a99] text-white px-6 py-3 rounded-md inline-block transition-colors"
              >
                <FaWhatsapp className="mr-2" /> Solicitar Soporte
              </a>
              <Link
                to="/distribuidores"
                className="flex items-center bg-white hover:bg-gray-100 text-[#003171] px-6 py-3 rounded-md inline-block transition-colors"
              >
                Distribuidores
              </Link>
            </div>
          </div>
        </div>
      </section>
      
      {/* Contact Information Cards */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8 mb-16">
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <h2 className="text-2xl font-bold mb-6 text-[#003171]">Ventas</h2>
                <div className="space-y-6">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-[#0066CC]/10 flex items-center justify-center mr-4">
                      <FaPhone className="text-[#0066CC]" />
                    </div>
                    <div>
                      <h3 className="text-gray-600 font-medium mb-1">Teléfono</h3>
                      <a href="tel:+525539908312" className="text-lg font-semibold text-gray-900 hover:text-[#0066CC]">
                        (55) 3990.8312
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-[#0066CC]/10 flex items-center justify-center mr-4">
                      <FaWhatsapp className="text-[#0066CC]" />
                    </div>
                    <div>
                      <h3 className="text-gray-600 font-medium mb-1">WhatsApp</h3>
                      <a href="https://wa.me/525539908312" target="_blank" rel="noopener noreferrer" className="text-lg font-semibold text-gray-900 hover:text-[#0066CC]">
                        (55) 3990.8312
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-[#0066CC]/10 flex items-center justify-center mr-4">
                      <FaEnvelope className="text-[#0066CC]" />
                    </div>
                    <div>
                      <h3 className="text-gray-600 font-medium mb-1">Email</h3>
                      <a href="mailto:ventas@ecosoft.com.mx" className="text-lg font-semibold text-gray-900 hover:text-[#0066CC]">
                        ventas@ecosoft.com.mx
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <h2 className="text-2xl font-bold mb-6 text-[#003171]">Soporte</h2>
                <div className="space-y-6">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-[#0066CC]/10 flex items-center justify-center mr-4">
                      <FaPhone className="text-[#0066CC]" />
                    </div>
                    <div>
                      <h3 className="text-gray-600 font-medium mb-1">Teléfono</h3>
                      <a href="tel:+525579658522" className="text-lg font-semibold text-gray-900 hover:text-[#0066CC]">
                        (55) 7965.8522
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-[#0066CC]/10 flex items-center justify-center mr-4">
                      <FaWhatsapp className="text-[#0066CC]" />
                    </div>
                    <div>
                      <h3 className="text-gray-600 font-medium mb-1">WhatsApp</h3>
                      <a href="https://wa.me/525579658522" target="_blank" rel="noopener noreferrer" className="text-lg font-semibold text-gray-900 hover:text-[#0066CC]">
                        (55) 7965.8522
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-[#0066CC]/10 flex items-center justify-center mr-4">
                      <FaEnvelope className="text-[#0066CC]" />
                    </div>
                    <div>
                      <h3 className="text-gray-600 font-medium mb-1">Email</h3>
                      <a href="mailto:soporte@ecosoft.com.mx" className="text-lg font-semibold text-gray-900 hover:text-[#0066CC]">
                        soporte@ecosoft.com.mx
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Address Section */}
            <div className="bg-white p-8 rounded-lg shadow-lg mb-12">
              <div className="flex flex-col md:flex-row items-start gap-6 md:gap-10">
                <div className="md:w-1/3">
                  <div className="flex items-start mb-4">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-[#0066CC]/10 flex items-center justify-center mr-4">
                      <FaMapMarkerAlt className="text-[#0066CC]" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-[#003171]">Ubicación</h2>
                    </div>
                  </div>
                  
                  <div className="ml-14">
                    <p className="text-gray-700 mb-2 font-semibold">Oficinas Centrales:</p>
                    <address className="text-gray-600 not-italic">
                      Watteau 54, Nonoalco Mixcoac,<br />
                      Benito Juárez, CP 03700,<br />
                      Ciudad de México, México
                    </address>
                    
                    <div className="mt-4">
                      <p className="text-gray-700 mb-2 font-semibold">Horario de Atención:</p>
                      <p className="text-gray-600">
                        Lunes a Viernes<br />
                        9:00 AM - 6:00 PM
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="md:w-2/3">
                  <div className="bg-gray-200 rounded-lg h-64 w-full">
                    <iframe 
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3763.4847124275485!2d-99.18774762394521!3d19.38985594688172!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x85d1ff7049a8be71%3A0x9a7f1b9b514de324!2sWatteau%2054%2C%20Mixcoac%2C%20Benito%20Ju%C3%A1rez%2C%2003700%20Ciudad%20de%20M%C3%A9xico%2C%20CDMX%2C%20M%C3%A9xico!5e0!3m2!1ses-419!2sus!4v1698361565848!5m2!1ses-419!2sus" 
                      width="100%" 
                      height="100%" 
                      className="rounded-lg"
                      style={{ border: 0 }}
                      allowFullScreen="" 
                      loading="lazy" 
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Ubicación de ECOSOFT"
                    ></iframe>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Social and Links */}
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <h2 className="text-2xl font-bold mb-6 text-[#003171]">Redes Sociales</h2>
                <p className="text-gray-600 mb-6">
                  Síguenos en nuestras redes sociales para estar al día con las últimas novedades, eventos y actualizaciones.
                </p>
                <div className="grid grid-cols-3 gap-3">
                  {socialLinks.map((social, index) => (
                    <a 
                      key={index}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex flex-col items-center justify-center p-4 rounded-lg bg-gray-50 hover:bg-[#0066CC]/10 transition-colors"
                    >
                      <span className="text-xl text-[#0066CC] mb-2">{social.icon}</span>
                      <span className="text-sm font-medium text-gray-700">{social.name}</span>
                    </a>
                  ))}
                </div>
              </div>
              
              <div className="bg-white p-8 rounded-lg shadow-lg">
                <h2 className="text-2xl font-bold mb-6 text-[#003171]">Enlaces Importantes</h2>
                <div className="space-y-4">
                  {companyLinks.map((link, index) => (
                    link.isExternal ? (
                      <a
                        key={index}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center p-3 rounded-lg bg-gray-50 hover:bg-[#0066CC]/10 transition-colors"
                      >
                        <span className="text-[#0066CC] mr-3">{link.icon}</span>
                        <span className="font-medium text-gray-700">{link.name}</span>
                        <FaArrowRight className="text-[#0066CC] ml-auto" />
                      </a>
                    ) : (
                      <Link 
                        key={index}
                        to={link.url}
                        className="flex items-center p-3 rounded-lg bg-gray-50 hover:bg-[#0066CC]/10 transition-colors"
                      >
                        <span className="text-[#0066CC] mr-3">{link.icon}</span>
                        <span className="font-medium text-gray-700">{link.name}</span>
                        <FaArrowRight className="text-[#0066CC] ml-auto" />
                      </Link>
                    )
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-16 bg-[#003171] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">¿Listo para dar el siguiente paso?</h2>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Adquiere OPUS hoy mismo y transforma la manera en que gestionas tus proyectos de construcción.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link 
              to="/distribuidores"
              className="px-8 py-3 bg-white hover:bg-gray-100 text-[#003171] font-medium rounded-md transition-colors"
            >
              Encuentra un Distribuidor
            </Link>
            <a 
              href="https://wa.me/525539908312"
              target="_blank"
              rel="noopener noreferrer" 
              className="px-8 py-3 border border-white hover:bg-white hover:text-[#003171] text-white font-medium rounded-md transition-colors flex items-center justify-center"
            >
              <FaWhatsapp className="mr-2" /> Solicitar Información
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactInfo; 